import { create } from "zustand";

export type PanelTab = "plan" | "places" | "sources" | "checklist" | "budget" | "trip";
export type MapLayer = "light" | "smooth" | "topo" | "dark" | "smoothDark" | "satellite" | "osm";
export type MobileView = "map" | "panel" | "chat";

export interface FocusRequest {
  bounds?: [[number, number], [number, number]];
  center?: [number, number];
  zoom?: number;
  placeIds?: string[];
  key: number;
}

/** Черновик нового места (с карты, из поиска или «умного добавления») */
export interface PlaceDraft {
  name?: string;
  nameLocal?: string | null;
  lat?: number | null;
  lng?: number | null;
  category?: import("@shared/types").PlaceCategory;
  region?: string | null;
  address?: string | null;
  description?: string | null;
  url?: string | null;
  phone?: string | null;
  /** найти адрес по координатам */
  reverse?: boolean;
  /** сразу разобрать этот текст/ссылку ИИ */
  smartText?: string;
  day?: string;
}

interface Toast {
  id: number;
  text: string;
  tone?: "info" | "error" | "success";
  action?: { label: string; run: () => void };
}

interface UIState {
  tab: PanelTab;
  selectedDay: string | null;
  selectedPlaceId: string | null;
  selectedItemId: string | null;
  hoverPlaceId: string | null;
  chatOpen: boolean;
  panelOpen: boolean;
  mobileView: MobileView;
  sheet: "peek" | "half" | "full";
  layer: MapLayer;
  showIdeas: boolean;
  showRoute: boolean;
  focus: FocusRequest | null;
  addMode: boolean;
  candidateSourceId: string | null;
  widgetOpenId: string | null;
  placeDraft: PlaceDraft | null;
  showSocial: boolean;
  presence: string[];
  toasts: Toast[];
  theme: "light" | "dark" | "system";
  set: (p: Partial<UIState>) => void;
  selectDay: (d: string | null) => void;
  selectPlace: (id: string | null, opts?: { focus?: boolean }) => void;
  focusMap: (f: Omit<FocusRequest, "key">) => void;
  toast: (text: string, opts?: Omit<Toast, "id" | "text">) => void;
  dismissToast: (id: number) => void;
}

const savedLayer = (() => {
  try {
    return (localStorage.getItem("tb:layer") as MapLayer) || null;
  } catch {
    return null;
  }
})();
const savedTheme = (() => {
  try {
    return (localStorage.getItem("tb:theme") as UIState["theme"]) || "system";
  } catch {
    return "system";
  }
})();

let toastId = 1;

export const useUI = create<UIState>((set, get) => ({
  tab: "plan",
  selectedDay: null,
  selectedPlaceId: null,
  selectedItemId: null,
  hoverPlaceId: null,
  chatOpen: typeof window !== "undefined" && window.innerWidth >= 1280,
  panelOpen: true,
  mobileView: "map",
  sheet: "peek",
  layer: savedLayer ?? "light",
  showIdeas: true,
  showRoute: true,
  focus: null,
  addMode: false,
  candidateSourceId: null,
  widgetOpenId: null,
  placeDraft: null,
  showSocial: true,
  presence: [],
  toasts: [],
  theme: savedTheme,
  set: (p) => {
    if (p.layer) {
      try {
        localStorage.setItem("tb:layer", p.layer);
      } catch {
        /* ignore */
      }
    }
    if (p.theme) {
      try {
        localStorage.setItem("tb:theme", p.theme);
      } catch {
        /* ignore */
      }
    }
    set(p);
  },
  selectDay: (d) => set({ selectedDay: d, selectedItemId: null }),
  selectPlace: (id) => {
    set({ selectedPlaceId: id });
    if (id && get().mobileView === "map") set({ sheet: get().sheet === "peek" ? "half" : get().sheet });
  },
  focusMap: (f) => set({ focus: { ...f, key: Date.now() + Math.random() } }),
  toast: (text, opts) => {
    const id = toastId++;
    set({ toasts: [...get().toasts, { id, text, ...opts }] });
    setTimeout(() => get().dismissToast(id), opts?.action ? 8000 : 4000);
  },
  dismissToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}));

export const toast = (text: string, opts?: Omit<Toast, "id" | "text">) => useUI.getState().toast(text, opts);
